import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { getFieldPropertiesByTarget } from '@wix/form-viewer/controller';
import { createOrderAndCharge } from '@wix/ambassador-ecom-v1-checkout/http';
import { ambassadorWithHeaders, getHeaders } from '../../utils/ambassador.utils';
import { FORMS_TEMPLATE_IDS, FQDN, FormFieldTarget, ECOM_PLATFORM_APP_DEF_ID } from '../../utils/consts';
import { createClient } from '@wix/sdk/client';
import * as checkout from '@wix/auto_sdk_ecom_checkout';
import { mapCheckoutModelToCheckoutSdk, mapCheckoutSdkToCheckout } from '../../utils/mapCheckoutSdkToCheckout';
import { SPECS } from '../SPECS';
import { CheckoutModel } from '../../models/Checkout.model';
export class CheckoutService {
  constructor(httpClient, errorHandler, controllerConfig, getCurrency) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
    this.controllerConfig = controllerConfig;
    _defineProperty(this, "requestWithHeaders", void 0);
    _defineProperty(this, "shouldUseModelInExpress", void 0);
    _defineProperty(this, "checkoutApi", void 0);
    _defineProperty(this, "sdkCheckoutForDelivery", void 0);
    _defineProperty(this, "getApiBaseUrl", controllerConfig => {
      const isSSR = controllerConfig.wixCodeApi.window.rendering.env === 'backend';
      if (isSSR) {
        return '';
      }
      return location.host;
    });
    const shouldUseWithErrorHandler = controllerConfig.essentials.experiments.enabled(SPECS.ErrorHandlerOnCheckout);
    this.shouldUseModelInExpress = controllerConfig.essentials.experiments.enabled(SPECS.UseConvertedPricesInExpress);
    this.requestWithHeaders = errorCodesMap => ambassadorWithHeaders(httpClient, controllerConfig, getCurrency(), errorHandler, shouldUseWithErrorHandler, errorCodesMap);
    let ecomInstance = controllerConfig.appParams.instance;
    controllerConfig.wixCodeApi.site.onInstanceChanged(_ref => {
      let {
        instance
      } = _ref;
      ecomInstance = instance;
    }, ECOM_PLATFORM_APP_DEF_ID);
    const wixClient = createClient({
      auth: {
        getAuthHeaders: () => ({
          headers: {
            Authorization: ecomInstance,
            ...getHeaders(controllerConfig, getCurrency())
          }
        })
      },
      headers: {},
      modules: {
        checkout: {
          getCheckout: checkout.getCheckout,
          updateCheckout: checkout.updateCheckout
        }
      },
      host: {
        apiBaseUrl: this.getApiBaseUrl(controllerConfig)
      }
    });
    this.checkoutApi = wixClient.checkout;
  }
  async getCheckoutByCheckoutId(checkoutId) {
    var _this$checkoutApi;
    const response = await ((_this$checkoutApi = this.checkoutApi) == null ? void 0 : _this$checkoutApi.getCheckout(checkoutId));
    return this.setCheckoutSdkForDeliveryAndReturnCheckoutModel(response);
  }
  setCheckoutSdkForDeliveryAndReturnCheckoutModel(response) {
    if (this.shouldUseModelInExpress) {
      this.sdkCheckoutForDelivery = response;
      const modelContext = {
        useConvertedPrices: response.conversionCurrency !== response.currency && response.paymentCurrency === response.conversionCurrency,
        experiments: this.controllerConfig.essentials.experiments
      };
      return CheckoutModel.fromV1(response, modelContext);
    }
    this.sdkCheckoutForDelivery = mapCheckoutModelToCheckoutSdk(mapCheckoutSdkToCheckout(response));
    return mapCheckoutSdkToCheckout(response);
  }
  async doesFormHaveRequiredDataExtendedFields() {
    const fieldProps = await getFieldPropertiesByTarget({
      options: {
        fqdn: FQDN
      },
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler
    });
    const fieldValues = fieldProps ? Object.values(fieldProps) : [];
    return fieldValues.filter(fieldValue => fieldValue == null ? void 0 : fieldValue.required).length > 0;
  }
  async doesFormHaveRequiredAdditionalInfoField() {
    var _fieldProps$FormField;
    const fieldProps = await getFieldPropertiesByTarget({
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler
    });
    return Boolean(fieldProps && ((_fieldProps$FormField = fieldProps[FormFieldTarget.additional_info_1]) == null ? void 0 : _fieldProps$FormField.required));
  }
  async createOrderAndCharge(_ref2) {
    let {
      id,
      paymentToken,
      urlParams
    } = _ref2;
    let orderId;
    let paymentResponseToken;
    try {
      const request = {
        id,
        paymentToken,
        urlParams
      };
      const {
        data
      } = await this.requestWithHeaders({})(createOrderAndCharge(request));
      orderId = data.orderId;
      paymentResponseToken = data.paymentResponseToken;
    } catch {
      orderId = undefined;
      paymentResponseToken = undefined;
    }
    return {
      orderId,
      paymentResponseToken
    };
  }
  async updateCheckoutForShippingEstimation(checkoutId, address) {
    var _this$checkoutApi2;
    const response = await ((_this$checkoutApi2 = this.checkoutApi) == null ? void 0 : _this$checkoutApi2.updateCheckout(checkoutId, {
      shippingInfo: {
        shippingDestination: {
          address: {
            country: (address == null ? void 0 : address.country) ?? null,
            city: (address == null ? void 0 : address.city) ?? null,
            postalCode: (address == null ? void 0 : address.postalCode) ?? null,
            subdivision: (address == null ? void 0 : address.subdivision) ?? null,
            streetAddress: null,
            addressLine1: null,
            addressLine2: null
          }
        }
      }
    }));
    return this.setCheckoutSdkForDeliveryAndReturnCheckoutModel(response);
  }
  async updateCheckoutByShippingContacts(checkoutId, shouldUpdateShippingAddress, billingContact, shippingContact) {
    var _this$checkoutApi3, _shippingContact$name, _shippingContact$name2;
    const response = await ((_this$checkoutApi3 = this.checkoutApi) == null ? void 0 : _this$checkoutApi3.updateCheckout(checkoutId, {
      buyerInfo: {
        email: billingContact == null ? void 0 : billingContact.email
      },
      shippingInfo: {
        shippingDestination: {
          ...(shouldUpdateShippingAddress && {
            address: {
              country: (shippingContact == null ? void 0 : shippingContact.country) ?? null,
              subdivision: (shippingContact == null ? void 0 : shippingContact.subdivision) ?? null,
              postalCode: (shippingContact == null ? void 0 : shippingContact.zipCode) ?? null,
              city: (shippingContact == null ? void 0 : shippingContact.city) ?? null,
              streetAddress: null,
              addressLine1: (shippingContact == null ? void 0 : shippingContact.addressLine1) ?? null,
              addressLine2: (shippingContact == null ? void 0 : shippingContact.addressLine2) ?? null
            }
          }),
          contactDetails: {
            firstName: (shippingContact == null || (_shippingContact$name = shippingContact.name) == null ? void 0 : _shippingContact$name.firstName) ?? null,
            lastName: (shippingContact == null || (_shippingContact$name2 = shippingContact.name) == null ? void 0 : _shippingContact$name2.lastName) ?? null,
            phone: (shippingContact == null ? void 0 : shippingContact.phoneNumber) ?? null,
            company: null,
            vatId: null
          }
        }
      }
    }));
    return this.setCheckoutSdkForDeliveryAndReturnCheckoutModel(response);
  }
  async updateCheckoutByBillingContacts(checkoutId, billingContact) {
    var _this$checkoutApi4;
    const response = await ((_this$checkoutApi4 = this.checkoutApi) == null ? void 0 : _this$checkoutApi4.updateCheckout(checkoutId, {
      billingInfo: {
        address: {
          country: (billingContact == null ? void 0 : billingContact.countryCode) ?? null,
          postalCode: (billingContact == null ? void 0 : billingContact.postalCode) ?? null,
          city: (billingContact == null ? void 0 : billingContact.city) ?? null,
          subdivision: (billingContact == null ? void 0 : billingContact.state) ?? null,
          streetAddress: null,
          addressLine1: null,
          addressLine2: null
        },
        contactDetails: {
          firstName: (billingContact == null ? void 0 : billingContact.firstName) ?? null,
          lastName: (billingContact == null ? void 0 : billingContact.lastName) ?? null,
          phone: (billingContact == null ? void 0 : billingContact.phone) ?? null,
          company: null,
          vatId: null
        }
      }
    }));
    return this.setCheckoutSdkForDeliveryAndReturnCheckoutModel(response);
  }
  async setShippingOption(checkoutId, shippingOptionId, fixCarrierIdForSelectShippingOptionExpress) {
    var _this$checkoutApi5;
    const response = await ((_this$checkoutApi5 = this.checkoutApi) == null ? void 0 : _this$checkoutApi5.updateCheckout(checkoutId, {
      shippingInfo: {
        selectedCarrierServiceOption: {
          code: shippingOptionId,
          ...(fixCarrierIdForSelectShippingOptionExpress && {
            carrierId: null
          })
        }
      }
    }));
    return this.setCheckoutSdkForDeliveryAndReturnCheckoutModel(response);
  }
}
//# sourceMappingURL=CheckoutService.js.map